import { createFocusTrap, type FocusTrap } from 'focus-trap';
import { BREAKPOINT_MEDIA_QUERIES } from '@/js/utils/breakpoints';
import { defineModule, nextTick } from '@/js/utils/helpers';
import { Overlay, toggleOverlay } from '@/js/utils/overlays';

let navbarFocusTrap: FocusTrap | null = null;

const MENU_TIMING = 500;

const getElements = () => ({
  navbar: document.querySelector<HTMLElement>('.navbar'),
  navbarBackdrop: document.getElementById('navbar__backdrop'),
  navbarMenu: document.getElementById('navbar__menu'),
  navbarToggle: document.querySelector<HTMLButtonElement>(
    '.navbar__nav > button[aria-controls]',
  ),
  navbarStylingElements: document.querySelectorAll<HTMLElement>(
    '[data-brand],[data-toggle],.content--block',
  ),
});

const toggleNavTransitions = (force: boolean) => {
  const { navbar } = getElements();
  if (!navbar) return;

  navbar.classList.toggle('navbar--no-transitions', force);
};

let closingTimeout: NodeJS.Timeout;
const toggleNavButton = (
  e: Event,
  force?: boolean,
  button?: HTMLButtonElement,
) => {
  const { navbar, navbarBackdrop, navbarMenu } = getElements();
  if (!navbar || !navbarBackdrop || !navbarMenu) return;

  const btn = button ?? (e.currentTarget as HTMLButtonElement);
  const expand = force ?? btn.ariaExpanded === 'false';
  const closing = !expand && btn.ariaExpanded === 'true';

  toggleOverlay(Overlay.MENU, expand);
  navbar.classList.toggle('menu--open', expand);
  navbarBackdrop.ariaHidden = String(!expand);
  navbarBackdrop.ariaHidden = String(!expand);

  clearTimeout(closingTimeout);
  if (closing) {
    navbar.classList.add('menu--closing');
    closingTimeout = setTimeout(() => {
      navbar.classList.remove('menu--closing');
    }, MENU_TIMING);
  } else {
    navbar.classList.remove('menu--closing');
  }

  btn.ariaExpanded = String(expand);
  btn.ariaLabel = expand ? 'Sluit menu' : 'Open menu';

  if (expand) {
    navbarFocusTrap?.activate();
  } else {
    navbarFocusTrap?.deactivate();
  }
};

const closeMenu = (e: Event) => {
  const { navbar, navbarToggle } = getElements();
  if (!navbar || !navbarToggle) return;

  if (e.type === 'mouseleave' && !BREAKPOINT_MEDIA_QUERIES.md.matches) return;

  toggleNavButton(e, false, navbarToggle);
};

const onBackdropClick = (e: Event) => {
  const { navbarMenu } = getElements();
  if (!navbarMenu || !e.target) return;

  if (navbarMenu.contains(e.target as Node)) return;

  closeMenu(e);
};

const navbarBreakpointChecker = (e: Event) => {
  const { navbarToggle: menuToggle } = getElements();
  if (!menuToggle) return;

  toggleNavTransitions(true);

  closeMenu(e);

  nextTick(() => {
    toggleNavTransitions(false);
  });
};

export default defineModule(
  () => {
    const { navbar, navbarToggle, navbarBackdrop } = getElements();
    if (!navbar || !navbarToggle || !navbarBackdrop) return;

    toggleNavTransitions(true);

    navbarFocusTrap = createFocusTrap(navbar, {
      allowOutsideClick: false,
      escapeDeactivates: true,
      onDeactivate: () => {
        // already closing
        if (navbar.classList.contains('menu--closing')) return;

        closeMenu(new Event('focustrap:deactivate'));
      },
    });

    navbarToggle.addEventListener('click', toggleNavButton, { passive: true });
    navbarBackdrop.addEventListener('click', onBackdropClick, {
      passive: true,
    });

    document.addEventListener('swup:link:self', closeMenu, { passive: true });
    document.addEventListener('swup:link:anchor', closeMenu, { passive: true });

    BREAKPOINT_MEDIA_QUERIES.lg.addEventListener(
      'change',
      navbarBreakpointChecker,
      { passive: true },
    );

    nextTick(() => {
      toggleNavTransitions(false);
    });
  },
  () => {
    const { navbar, navbarBackdrop, navbarToggle } = getElements();
    if (!navbar || !navbarBackdrop || !navbarToggle) return;

    navbarFocusTrap?.deactivate({
      returnFocus: false,
      onDeactivate: () => {
        // do nothing
      },
    });

    navbarToggle.addEventListener('click', toggleNavButton, { passive: true });
    navbarBackdrop.removeEventListener('click', onBackdropClick);

    document.removeEventListener('swup:link:self', closeMenu);
    document.removeEventListener('swup:link:anchor', closeMenu);

    BREAKPOINT_MEDIA_QUERIES.md.removeEventListener(
      'change',
      navbarBreakpointChecker,
    );

    toggleOverlay(Overlay.MENU, false);
  },
);
